import { useState, useEffect } from 'react';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';

export default function NewBuildManagement() {
  const [isNavVisible, setIsNavVisible] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToContact = () => {
    window.REACT_APP_NAVIGATE('/');
    setTimeout(() => {
      const contactSection = document.getElementById('contact');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const services = [
    'Shipyard identification and selection',
    'Contract review and build specification development',
    'Budget oversight and payment milestone management',
    'Technical supervision and build monitoring',
    'Liaison between owner, designers, shipyard, and classification societies',
    'Sea trials, commissioning, and final delivery'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation isVisible={isNavVisible} setIsVisible={setIsNavVisible} />
      
      {/* Hero Section */}
      <section className="relative h-screen w-full flex items-center justify-center">
        <div className="absolute inset-0 w-full h-full">
          <img 
            src="https://static.readdy.ai/image/af0cbc08a3b78af074c92730eb798524/fea9132fa397fc97b73730524a2b87fc.jpeg"
            alt="Yacht construction in shipyard"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>

        <div className="relative z-10 text-center px-8 max-w-5xl mx-auto">
          <h1 className="font-serif text-6xl md:text-7xl lg:text-8xl text-white mb-8 tracking-tight">
            New Build Management
          </h1>
          <p className="text-lg md:text-xl lg:text-2xl text-white/95 mb-12 max-w-3xl mx-auto leading-relaxed font-light">
            Expert guidance from concept to delivery, ensuring precision, transparency, and uncompromising standards.
          </p>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-32 px-8 lg:px-16 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="w-20 h-px bg-black mb-8"></div>
          <p className="text-lg md:text-xl text-gray-800 leading-relaxed mb-8">
            SYG Yachts provides comprehensive new build management services for clients undertaking the construction of custom and semi-custom yachts. Acting as an independent owner's representative, we guide clients through every stage of the build process — from initial concept and shipyard selection to final delivery.
          </p>
          <p className="text-lg md:text-xl text-gray-800 leading-relaxed">
            Our role is to protect the client's interests, manage complexity, and ensure the finished yacht reflects the highest standards of quality, performance, and value.
          </p>
        </div>
      </section>

      {/* Scope of Services */}
      <section className="py-32 px-8 lg:px-16 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="mb-20">
            <div className="w-20 h-px bg-black mb-8"></div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-black leading-tight mb-12">
              Scope of Services
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {services.map((service, index) => (
              <div key={index} className="flex items-start gap-6">
                <div className="w-2 h-2 bg-black mt-3 flex-shrink-0"></div>
                <p className="text-lg md:text-xl text-gray-800 leading-relaxed">
                  {service}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why New Build Management Matters */}
      <section className="py-32 px-8 lg:px-16 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="mb-12">
            <div className="w-20 h-px bg-black mb-8"></div>
            <h2 className="font-serif text-4xl md:text-5xl text-black leading-tight mb-12">
              Why New Build Management Matters
            </h2>
          </div>
          <p className="text-lg md:text-xl text-gray-800 leading-relaxed">
            Building a yacht is a highly technical and capital-intensive process. Independent oversight is essential to manage risk, maintain timelines, and ensure transparency. SYG Yachts brings structure, discipline, and informed decision-making to every project, allowing owners to proceed with confidence.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="relative py-32 px-8 lg:px-16">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://static.readdy.ai/image/af0cbc08a3b78af074c92730eb798524/56d73fd1c84c20ea8d702bbd12b87e8f.jpeg')`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white mb-6 leading-tight">
              New Build Inquiry
            </h2>
            <p className="text-lg md:text-xl text-white/90 leading-relaxed">
              Share your vision with us and our expert team will guide you through every step of your new build journey.
            </p>
          </div>
          
          <form 
            id="new-build-inquiry-form"
            data-readdy-form
            className="bg-white/95 backdrop-blur-sm p-8 md:p-12"
            onSubmit={async (e) => {
              e.preventDefault();
              const form = e.currentTarget;
              const formData = new FormData(form);
              const submitBtn = form.querySelector('button[type="submit"]') as HTMLButtonElement;
              const originalText = submitBtn.innerText;
              
              try {
                submitBtn.disabled = true;
                submitBtn.innerText = 'SUBMITTING...';
                
                const response = await fetch('https://readdy.ai/api/form/d633605anburc340qan0', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                  },
                  body: new URLSearchParams(formData as any).toString(),
                });
                
                if (response.ok) {
                  submitBtn.innerText = 'SUBMITTED SUCCESSFULLY';
                  form.reset();
                  setTimeout(() => {
                    submitBtn.innerText = originalText;
                    submitBtn.disabled = false;
                  }, 3000);
                } else {
                  throw new Error('Submission failed');
                }
              } catch (error) {
                submitBtn.innerText = 'SUBMISSION FAILED';
                setTimeout(() => {
                  submitBtn.innerText = originalText;
                  submitBtn.disabled = false;
                }, 3000);
              }
            }}
          >
            {/* Personal Information */}
            <div className="mb-10">
              <h3 className="text-sm tracking-widest text-gray-500 mb-6 uppercase">Contact Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm text-gray-700 mb-2">Full Name *</label>
                  <input 
                    type="text" 
                    name="full_name" 
                    required
                    className="w-full px-4 py-3 border border-gray-300 focus:border-black focus:outline-none transition-colors text-sm"
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-2">Email Address *</label>
                  <input 
                    type="email" 
                    name="email" 
                    required
                    className="w-full px-4 py-3 border border-gray-300 focus:border-black focus:outline-none transition-colors text-sm"
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-2">Phone Number *</label>
                  <input 
                    type="tel" 
                    name="phone" 
                    required
                    className="w-full px-4 py-3 border border-gray-300 focus:border-black focus:outline-none transition-colors text-sm"
                    placeholder="+1 234 567 8900"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-2">Country of Residence</label>
                  <input 
                    type="text" 
                    name="country" 
                    className="w-full px-4 py-3 border border-gray-300 focus:border-black focus:outline-none transition-colors text-sm"
                    placeholder="Your country"
                  />
                </div>
              </div>
            </div>

            {/* Yacht Specifications */}
            <div className="mb-10">
              <h3 className="text-sm tracking-widest text-gray-500 mb-6 uppercase">Yacht Specifications</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm text-gray-700 mb-2">Yacht Type *</label>
                  <select 
                    name="yacht_type" 
                    required
                    className="w-full px-4 py-3 border border-gray-300 focus:border-black focus:outline-none transition-colors text-sm bg-white cursor-pointer"
                  >
                    <option value="">Select yacht type</option>
                    <option value="Motor Yacht">Motor Yacht</option>
                    <option value="Sailing Yacht">Sailing Yacht</option>
                    <option value="Explorer Yacht">Explorer Yacht</option>
                    <option value="Catamaran">Catamaran</option>
                    <option value="Sport Yacht">Sport Yacht</option>
                    <option value="Classic Yacht">Classic Yacht</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-2">Desired Length (meters) *</label>
                  <select 
                    name="yacht_length" 
                    required
                    className="w-full px-4 py-3 border border-gray-300 focus:border-black focus:outline-none transition-colors text-sm bg-white cursor-pointer"
                  >
                    <option value="">Select length range</option>
                    <option value="30-40m">30 - 40 meters</option>
                    <option value="40-50m">40 - 50 meters</option>
                    <option value="50-60m">50 - 60 meters</option>
                    <option value="60-80m">60 - 80 meters</option>
                    <option value="80-100m">80 - 100 meters</option>
                    <option value="100m+">100+ meters</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-2">Hull Material Preference</label>
                  <select 
                    name="hull_material" 
                    className="w-full px-4 py-3 border border-gray-300 focus:border-black focus:outline-none transition-colors text-sm bg-white cursor-pointer"
                  >
                    <option value="">Select material</option>
                    <option value="Steel">Steel</option>
                    <option value="Aluminum">Aluminum</option>
                    <option value="Composite/GRP">Composite/GRP</option>
                    <option value="Carbon Fiber">Carbon Fiber</option>
                    <option value="No Preference">No Preference</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-2">Number of Guest Cabins</label>
                  <select 
                    name="guest_cabins" 
                    className="w-full px-4 py-3 border border-gray-300 focus:border-black focus:outline-none transition-colors text-sm bg-white cursor-pointer"
                  >
                    <option value="">Select number</option>
                    <option value="4">4 Cabins</option>
                    <option value="5">5 Cabins</option>
                    <option value="6">6 Cabins</option>
                    <option value="7">7 Cabins</option>
                    <option value="8+">8+ Cabins</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Project Details */}
            <div className="mb-10">
              <h3 className="text-sm tracking-widest text-gray-500 mb-6 uppercase">Project Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm text-gray-700 mb-2">Estimated Budget (EUR) *</label>
                  <select 
                    name="budget" 
                    required
                    className="w-full px-4 py-3 border border-gray-300 focus:border-black focus:outline-none transition-colors text-sm bg-white cursor-pointer"
                  >
                    <option value="">Select budget range</option>
                    <option value="€10M - €20M">€10M - €20M</option>
                    <option value="€20M - €40M">€20M - €40M</option>
                    <option value="€40M - €60M">€40M - €60M</option>
                    <option value="€60M - €100M">€60M - €100M</option>
                    <option value="€100M - €150M">€100M - €150M</option>
                    <option value="€150M+">€150M+</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-2">Desired Delivery Timeline</label>
                  <select 
                    name="timeline" 
                    className="w-full px-4 py-3 border border-gray-300 focus:border-black focus:outline-none transition-colors text-sm bg-white cursor-pointer"
                  >
                    <option value="">Select timeline</option>
                    <option value="2-3 years">2 - 3 years</option>
                    <option value="3-4 years">3 - 4 years</option>
                    <option value="4-5 years">4 - 5 years</option>
                    <option value="5+ years">5+ years</option>
                    <option value="Flexible">Flexible</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-2">Preferred Shipyard Region</label>
                  <select 
                    name="shipyard_region" 
                    className="w-full px-4 py-3 border border-gray-300 focus:border-black focus:outline-none transition-colors text-sm bg-white cursor-pointer"
                  >
                    <option value="">Select region</option>
                    <option value="Northern Europe">Northern Europe (Netherlands, Germany)</option>
                    <option value="Mediterranean">Mediterranean (Italy, Turkey)</option>
                    <option value="United States">United States</option>
                    <option value="Asia Pacific">Asia Pacific</option>
                    <option value="No Preference">No Preference</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-700 mb-2">Primary Cruising Area</label>
                  <select 
                    name="cruising_area" 
                    className="w-full px-4 py-3 border border-gray-300 focus:border-black focus:outline-none transition-colors text-sm bg-white cursor-pointer"
                  >
                    <option value="">Select area</option>
                    <option value="Mediterranean">Mediterranean</option>
                    <option value="Caribbean">Caribbean</option>
                    <option value="South Pacific">South Pacific</option>
                    <option value="Northern Europe">Northern Europe</option>
                    <option value="Worldwide">Worldwide / Explorer</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Special Requirements */}
            <div className="mb-10">
              <h3 className="text-sm tracking-widest text-gray-500 mb-6 uppercase">Special Requirements & Features</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {[
                  'Helipad',
                  'Beach Club',
                  'Swimming Pool',
                  'Spa/Wellness',
                  'Cinema Room',
                  'Gym',
                  'Tender Garage',
                  'Submarine',
                  'Dive Center',
                  'Touch & Go Helideck',
                  'Zero Speed Stabilizers',
                  'Hybrid Propulsion'
                ].map((feature) => (
                  <label key={feature} className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="checkbox" 
                      name="features" 
                      value={feature}
                      className="w-4 h-4 accent-black cursor-pointer"
                    />
                    <span className="text-sm text-gray-700">{feature}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Additional Information */}
            <div className="mb-10">
              <h3 className="text-sm tracking-widest text-gray-500 mb-6 uppercase">Additional Information</h3>
              <div>
                <label className="block text-sm text-gray-700 mb-2">Please describe your vision and any specific requirements</label>
                <textarea 
                  name="additional_details" 
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 focus:border-black focus:outline-none transition-colors text-sm resize-none"
                  placeholder="Tell us about your dream yacht, design preferences, lifestyle requirements, or any specific features you envision..."
                ></textarea>
                <p className="text-xs text-gray-400 mt-1">Maximum 500 characters</p>
              </div>
            </div>

            {/* How Did You Hear About Us */}
            <div className="mb-10">
              <label className="block text-sm text-gray-700 mb-2">How did you hear about SYG Yachts?</label>
              <select 
                name="referral_source" 
                className="w-full px-4 py-3 border border-gray-300 focus:border-black focus:outline-none transition-colors text-sm bg-white cursor-pointer"
              >
                <option value="">Select an option</option>
                <option value="Boat Show">Boat Show</option>
                <option value="Referral">Referral / Word of Mouth</option>
                <option value="Online Search">Online Search</option>
                <option value="Social Media">Social Media</option>
                <option value="Industry Publication">Industry Publication</option>
                <option value="Previous Client">Previous Client</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <button 
              type="submit"
              className="w-full py-5 bg-black text-white text-sm tracking-widest hover:bg-gray-900 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              SUBMIT NEW BUILD INQUIRY
            </button>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}
